import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { Observable, Subscription } from 'rxjs';

import { Component, OnDestroy, signal } from '@angular/core';

import { InlineSpinnerComponent } from '@mitsubishi/ui/spinner';
import { FormattingUtils } from '@mitsubishi/utils';

import { FreeStUtils } from '../../chiller-free-st.utils';
import { FreestFieldMappingDto, UomSystem } from '../../models/chiller-free-st.model';

export interface CopCellRendererParams {
  getUomSystem: () => UomSystem;
  getMapping: () => FreestFieldMappingDto | undefined;
  computeFn: (value: number, sourceUom: UomSystem) => Observable<number>;
}

@Component({
  selector: 'apm-cop-cell-renderer',
  imports: [InlineSpinnerComponent],
  template: `
    @if (loading()) {
      <div class="d-flex align-items-center w-100 h-100 justify-content-center">
        <apm-inline-spinner />
      </div>
    } @else {
      {{ displayValue() }}
    }
  `,
})
export class CopCellRendererComponent implements ICellRendererAngularComp, OnDestroy {
  readonly displayValue = signal('');
  readonly loading = signal(false);

  private sub: Subscription | null = null;

  agInit(params: ICellRendererParams): void {
    this.convert(params);
  }

  refresh(params: ICellRendererParams): boolean {
    this.convert(params);
    return true;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private convert(params: ICellRendererParams): void {
    this.sub?.unsubscribe();
    this.sub = null;

    const siValue = params.value as number;
    const rendererParams = params.colDef?.cellRendererParams as CopCellRendererParams | undefined;

    if (siValue == null || !rendererParams) {
      this.displayValue.set('');
      this.loading.set(false);
      return;
    }

    const uom = rendererParams.getUomSystem();
    const mapping = rendererParams.getMapping();

    if (uom === 'SI') {
      this.displayValue.set(this.format(siValue, mapping, uom));
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.sub = rendererParams.computeFn(siValue, 'SI').subscribe({
      next: displayVal => {
        this.loading.set(false);
        this.displayValue.set(this.format(displayVal, mapping, uom));
      },
      error: () => {
        this.loading.set(false);
        this.displayValue.set(String(siValue));
      },
    });
  }

  private format(value: number, mapping: FreestFieldMappingDto | undefined, uom: UomSystem): string {
    if (!mapping) return String(value);
    const formatting = FreeStUtils.getFormatting(mapping, uom);
    return FormattingUtils.formatOutputValue(formatting, value) ?? String(value);
  }
}
